import type { RoomSnapshot } from '../../lib/api/rooms'
import { useT } from '../../i18n'

interface Props { snapshot: RoomSnapshot }

const COURSE_NAMES = ['Starter', 'Main', 'Dessert'] as const
const COURSE_EMOJIS = ['🥗', '🍽️', '🍮']
const COURSE_HINTS = [
  'Everyone writes their starter on their phone.',
  'Now the main. Make it the centrepiece.',
  'End it on something sweet (or surprising).',
]

/**
 * Writing phase. Shows the course name huge and a live "X of Y
 * submitted" tally. Players write on their phones and the host advances
 * from theirs — nothing to do on the TV but watch the count tick up.
 */
export function TVBuildingPhase({ snapshot }: Props) {
  const t = useT()
  const { room, players, submissions } = snapshot

  const courseIdx = (room.active_course - 1) as 0 | 1 | 2
  const courseName = t(COURSE_NAMES[courseIdx])
  const courseEmoji = COURSE_EMOJIS[courseIdx]
  const courseHint = t(COURSE_HINTS[courseIdx])

  // A player has "submitted this course" if their submission row has
  // non-empty text for the current course. We reset these between
  // courses by overwriting on submit_single_course, so this column
  // check is accurate per-course.
  const hasSubmittedCourse = (playerId: string) => {
    const sub = submissions.find(s => s.player_id === playerId)
    if (!sub) return false
    const text =
      courseIdx === 0 ? sub.course1_text :
      courseIdx === 1 ? sub.course2_text :
                        sub.course3_text
    return Boolean(text && text.trim())
  }

  const submittedCount = players.filter(p => hasSubmittedCourse(p.id)).length
  const everyoneIn = players.length > 0 && submittedCount === players.length

  return (
    <div className="w-full max-w-6xl text-center">
      <p className="text-sm font-semibold uppercase tracking-widest text-brand-muted mb-3">
        Course {room.active_course} of 3 · Writing time
      </p>
      <h1 className="font-display text-8xl md:text-9xl font-bold text-brand-text mb-4 leading-none">
        {courseEmoji} {courseName}
      </h1>
      <p className="text-3xl text-brand-muted mb-12">{courseHint}</p>

      {/* Big progress */}
      <div className="mb-10">
        <p className="font-display text-7xl md:text-8xl font-bold text-brand-primary mb-2">
          {submittedCount} / {players.length}
        </p>
        <p className="text-2xl text-brand-muted">
          {everyoneIn ? t('Everyone is done!') : t('dishes submitted')}
        </p>
      </div>

      {/* Per-player status */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-10 max-w-4xl mx-auto">
        {players.map(p => {
          const done = hasSubmittedCourse(p.id)
          return (
            <div key={p.id}
                 className={`card p-4 text-xl font-semibold flex items-center justify-between
                             transition-all
                             ${done ? 'bg-iet-green/15 border-iet-green/40' : 'bg-white'}`}>
              <span className="truncate">{p.display_name}</span>
              <span className={done ? 'text-iet-green text-2xl' : 'text-brand-muted text-xl'}>
                {done ? '✓' : '…'}
              </span>
            </div>
          )
        })}
      </div>

      <p className="text-2xl text-brand-muted">
        {everyoneIn ? t('Starting presentations…') : t('Writing on their phones…')}
      </p>
    </div>
  )
}
